import ufeApi from 'services/api/ufeApi';
import { gqlClient } from 'services/api/gql/gqlClient';
import { DeleteAltPickupPerson } from 'constants/gql/mutations/checkout/deleteAltPickupPerson.gql';
import OrderUtils from 'utils/Order';

// https://confluence.sephora.com/wiki/pages/viewpage.action?spaceKey=ILLUMINATE&title=Remove+Alternate+Pickup+Person+Details+API

/**
 * Removes the alternate pickup person from an order.
 * Routes to OIS GraphQL (DeleteAltPickupPerson) or legacy OXS REST based on oisMigration feature flags.
 *
 * @param {string} [orderId='current'] - The order ID to remove alt pickup person from
 * @param {string} [requestOrigin=''] - Request origin identifier
 * @returns {Promise<Object>} The delete response data
 * @throws {Error} Throws with errorCode=-1 and errorMessages for OIS non-SUCCESS responses
 * @throws {Object} Throws raw error object for OXS errors
 */

/** @returns {boolean} Whether OIS modification migration is enabled */
const isOisEnabled = () =>
    Sephora?.configurationSettings?.oisMigration?.global?.isEnabled && Sephora?.configurationSettings?.oisMigration?.modification?.isEnabled;

/** @returns {boolean} Whether the request should be routed to OIS GraphQL */
const shouldUseOis = requestOrigin =>
    isOisEnabled() && requestOrigin === OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_DETAILS_PAGE;

const removeAlternatePickupPerson = async (orderId = 'current', requestOrigin = '') => {
    if (shouldUseOis(requestOrigin)) {
        const headers = requestOrigin ? { 'x-request-origin': requestOrigin } : undefined;

        const data = await gqlClient.query({
            operationName: 'DeleteAltPickupPerson',
            query: DeleteAltPickupPerson,
            variables: {
                input: { orderId }
            },
            headers
        });

        const result = data?.deleteAltPickupPerson;

        if (result?.responseCode && result.responseCode !== 'SUCCESS') {
            const error = new Error(result.message || 'Remove alternate pickup person failed');
            error.errorCode = -1;
            error.responseCode = result.responseCode;
            error.errorMessages = result.message ? [result.message] : [];

            throw error;
        }

        return data;
    }

    const requestOriginParamString = requestOrigin ? '?requestOrigin=' + requestOrigin : '';
    const url = `/api/checkout/orders/${orderId}/altPickupPerson${requestOriginParamString}`;

    const data = await ufeApi.makeRequest(url, { method: 'DELETE' });

    if (data?.errorCode) {
        throw data;
    }

    return data;
};

export default removeAlternatePickupPerson;
